"use client";

import * as React from "react";
import Image from "next/image";
import { createPortal } from "react-dom";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  ChevronDown,
  CircleHelp,
  Compass,
  Layers3,
  ListTodo,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

import {
  AccordionChevronCircle,
  type InsightTone,
  AreaScoreBadge,
  EvidencePill,
  InsightDetailLine,
  MetricBlock,
  MiniBadge,
  MiniCount,
  MiniOutlineBadge,
  OrdinalPill,
  avatarToneClasses,
  escapeRegex,
  fundamentalsAverageTone,
  getRatioPercent,
} from "@/components/site/sections/six-pillars-preview/charts-and-badges";
import { FundamentalsStage } from "@/components/site/sections/six-pillars-preview/fundamentals-stage";
import { PulseStage } from "@/components/site/sections/six-pillars-preview/pulse-stage";
import {
  sixPillarsCandidatesPreview,
  sixPillarsCoopProfilePreview,
  sixPillarsFundamentalsPreview,
  sixPillarsPreviewPeople,
  sixPillarsPulsePreview,
  getSixPillarsPreviewPerson,
  getPreviewEvidenceCount,
  type PreviewListItem,
  type PreviewPerson,
  type PreviewSection,
  type PreviewSignalGroups,
} from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

type InsightIconKey =
  | "compass"
  | "sparkles"
  | "shield"
  | "target"
  | "layers"
  | "users"
  | "chart";

type InsightSurfaceItemConfig = {
  id: string;
  title: string;
  preview: string;
  icon: InsightIconKey;
  tone?: InsightTone;
  defaultOpen?: boolean;
  content: React.ReactNode;
};

export const FloatingBoundaryContext = React.createContext<{
  boundaryRef: React.RefObject<HTMLDivElement | null>;
} | null>(null);

const insightIconMap: Record<
  InsightIconKey,
  React.ComponentType<{ className?: string }>
> = {
  compass: Compass,
  sparkles: Sparkles,
  shield: ShieldAlert,
  target: Target,
  layers: Layers3,
  users: Users,
  chart: TrendingUp,
};

const toneStyles: Record<
  InsightTone,
  {
    badgeClassName: string;
    iconWrapperClassName: string;
    iconClassName: string;
  }
> = {
  neutral: {
    badgeClassName: "border-slate-200 bg-white text-slate-700",
    iconWrapperClassName: "border-slate-200 bg-white",
    iconClassName: "text-slate-700",
  },
  positive: {
    badgeClassName: "border-emerald-200 bg-emerald-100/80 text-emerald-900",
    iconWrapperClassName: "border-emerald-200 bg-white",
    iconClassName: "text-emerald-600",
  },
  warning: {
    badgeClassName: "border-amber-200 bg-amber-100/80 text-amber-900",
    iconWrapperClassName: "border-amber-200 bg-white",
    iconClassName: "text-amber-600",
  },
  info: {
    badgeClassName: "border-sky-200 bg-sky-100/80 text-sky-900",
    iconWrapperClassName: "border-sky-200 bg-white",
    iconClassName: "text-sky-600",
  },
};

export function CoopProfilePreview({
  variant,
}: {
  variant: "compact" | "expanded";
}) {
  const compact = variant === "compact";
  const items = React.useMemo(
    (): InsightSurfaceItemConfig[] =>
      sixPillarsCoopProfilePreview.sections.map((section, index) => ({
        id: section.id,
        title: section.title,
        preview: section.preview,
        icon:
          section.id === "overall-assessment"
            ? "compass"
            : section.id === "team-potential" ||
                section.id === "promotion-candidates" ||
                section.id === "quick-wins"
              ? "sparkles"
              : section.id === "risk-areas" || section.id === "avoid-hiring"
                ? "shield"
                : section.id === "operational-guidance" ||
                    section.id === "swot-summary"
                  ? "layers"
                  : section.id === "hiring-needs" || section.id === "team-building"
                    ? "users"
                    : "target",
        tone:
          section.id === "team-potential" ||
          section.id === "promotion-candidates" ||
          section.id === "quick-wins"
            ? "positive"
            : section.id === "risk-areas" || section.id === "avoid-hiring"
              ? "warning"
              : "info",
        defaultOpen: index === 0,
        content: <AppSectionBody section={section} />,
      })),
    [],
  );

  return (
    <div
      className={cn(
        "space-y-5 px-4 py-4 sm:px-5",
        compact ? "sm:py-5" : "space-y-6 sm:px-6 sm:py-6",
      )}
    >
      <TeamMatrixPreview />
      <AppInsightSurface items={items} />
    </div>
  );
}

type MatrixFunctionKey = "D" | "R" | "C" | "Ca";
type MatrixPosition = 1 | 2 | 3 | 4;

type TeamMatrixCellDiagnosis = {
  position: MatrixPosition;
  functionKey: MatrixFunctionKey;
  count: number;
  heat: number;
};

const TEAM_MATRIX_FUNCTIONS: Array<{
  key: MatrixFunctionKey;
  label: string;
  shortLabel: string;
  headerClassName: string;
  shortLabelClassName: string;
}> = [
  {
    key: "D",
    label: "Drive",
    shortLabel: "D",
    headerClassName: "bg-sky-50/80",
    shortLabelClassName: "text-sky-700",
  },
  {
    key: "R",
    label: "Reasoning",
    shortLabel: "R",
    headerClassName: "bg-violet-50/80",
    shortLabelClassName: "text-violet-700",
  },
  {
    key: "C",
    label: "Connection",
    shortLabel: "C",
    headerClassName: "bg-amber-50/80",
    shortLabelClassName: "text-amber-700",
  },
  {
    key: "Ca",
    label: "Capacity",
    shortLabel: "Ca",
    headerClassName: "bg-emerald-50/80",
    shortLabelClassName: "text-emerald-700",
  },
];

const TEAM_MATRIX_POSITIONS: Array<{
  position: MatrixPosition;
  title: string;
  description: string;
}> = [
  {
    position: 1,
    title: "Punch",
    description: "default gear, shows under stress first",
  },
  {
    position: 2,
    title: "Foundation",
    description: "strong, steady, process-oriented",
  },
  {
    position: 3,
    title: "Sensitive",
    description: "cares, doubts, needs kind feedback",
  },
  {
    position: 4,
    title: "Background",
    description: "follows, keeps it simple",
  },
];

const TEAM_MATRIX_HEAT_CLASSES = [
  "bg-background",
  "bg-[#f7fbfd]",
  "bg-[#eaf6fc]",
  "bg-[#d8eef9]",
  "bg-[#c4e5f6]",
  "bg-[#b0dbf1]",
  "bg-[#98cee9]",
];

const TEAM_MATRIX_FUNCTION_INDEX = new Map<MatrixFunctionKey, number>(
  TEAM_MATRIX_FUNCTIONS.map((item, index) => [item.key, index]),
);

const TEAM_MATRIX_MEANINGS: Record<MatrixFunctionKey, Record<MatrixPosition, string>> = {
  D: {
    1: "Takes responsibility, makes decisions, holds boundaries, and moves things forward. In a team, this gives leadership, speed, and crisis stability.",
    2: "Helps the team agree on responsibility: who leads, who decides, who owns what. This prevents chaos and power struggles.",
    3: "Sensitive to pressure, public challenge, humiliation, or status games. Use one-to-one conversations, pauses, and small confidence-building steps.",
    4: "Does not naturally want to carry leadership or push decisions. Give clear tasks, deadlines, and success criteria.",
  },
  R: {
    1: "Speaks in conclusions, models, arguments, and criteria. In a team, this gives clarity and intellectual direction.",
    2: "Thinks well with others, asks questions, builds shared criteria, and creates common understanding.",
    3: "Sensitive around competence, reasoning, and whether the argument is clear enough. Give examples, preparation time, and safe wording.",
    4: "Prefers a ready-made model, checklist, or decision structure instead of building complex reasoning from scratch.",
  },
  C: {
    1: "Sets the tone, reads the room, and influences atmosphere. This brings energy, emotional presence, and engagement.",
    2: "Naturally hears people, bridges positions, and keeps conversations human. Valuable for feedback, conflict, and difficult conversations.",
    3: "Sensitive to tone, reactions, public comments, and emotional judgement. Feedback about tone should be private and gentle.",
    4: "Does not naturally hold the emotional fabric of a conversation. Give simple scripts for opening, closing, and asking for feedback.",
  },
  Ca: {
    1: "Strongly notices resources, body, money, conditions, comfort, and capacity. This brings realism and sustainable limits.",
    2: "Naturally makes the environment workable: rhythm, comfort, tools, operations, and practical stability.",
    3: "Sensitive to fatigue, workload, money, and practical chaos. Use predictable rhythm, soft pacing, and clear resource templates.",
    4: "Does not naturally want to think much about logistics, schedule, or physical resources. Use checklists, budgets, and weekly plans.",
  },
};

const MAX_VISIBLE_MATRIX_AVATARS = 4;

function TeamMatrixPreview() {
  const members = React.useMemo(
    () => sixPillarsPreviewPeople.filter((person) => person.type !== "candidate"),
    [],
  );
  const membersByCell = React.useMemo(() => buildTeamMatrixMembersByCell(members), [members]);
  const cellsByKey = React.useMemo(() => buildTeamMatrixCellsByKey(members), [members]);

  return (
    <section className="space-y-3" aria-label="Team Matrix">
      <div className="overflow-x-auto pb-1 site-scrollbar-auto">
        <div className="grid min-w-[46rem] grid-cols-[minmax(9.5rem,11rem)_repeat(4,minmax(9rem,1fr))] gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
          <div className="bg-slate-50/80 p-3 text-sm font-medium text-slate-600">
            Position
          </div>

          {TEAM_MATRIX_FUNCTIONS.map((functionDef) => (
            <div
              key={functionDef.key}
              className={cn("p-3", functionDef.headerClassName)}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-slate-950">
                  {functionDef.label}
                </span>
                <span
                  className={cn(
                    "text-[11px] font-semibold uppercase tracking-[0.08em]",
                    functionDef.shortLabelClassName,
                  )}
                >
                  {functionDef.shortLabel}
                </span>
              </div>
            </div>
          ))}

          {TEAM_MATRIX_POSITIONS.map(({ position, title, description }) => (
            <React.Fragment key={position}>
              <div className="bg-background p-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-[11px] font-medium tabular-nums text-slate-500">
                    {position}
                  </span>
                  <span className="text-sm font-medium text-slate-950">{title}</span>
                </div>
                <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
              </div>

              {TEAM_MATRIX_FUNCTIONS.map((functionDef, functionPosition) => {
                const cell = cellsByKey.get(`${position}:${functionDef.key}`);

                if (!cell) {
                  return null;
                }

                return (
                  <TeamMatrixCell
                    key={`${position}-${functionDef.key}`}
                    cell={cell}
                    members={membersByCell[position - 1]?.[functionPosition] ?? []}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamMatrixCell({
  cell,
  members,
}: {
  cell: TeamMatrixCellDiagnosis;
  members: PreviewPerson[];
}) {
  const hiddenAggregateCount = Math.max(0, cell.count - members.length);
  const hasOverflow = members.length > MAX_VISIBLE_MATRIX_AVATARS || hiddenAggregateCount > 0;
  const visibleMembers = members.slice(
    0,
    hasOverflow ? MAX_VISIBLE_MATRIX_AVATARS - 1 : MAX_VISIBLE_MATRIX_AVATARS,
  );
  const overflowCount = Math.max(0, members.length - visibleMembers.length) + hiddenAggregateCount;
  const functionLabel = TEAM_MATRIX_FUNCTIONS.find((item) => item.key === cell.functionKey)?.label ?? cell.functionKey;
  const position = TEAM_MATRIX_POSITIONS.find((item) => item.position === cell.position);
  const meaning = TEAM_MATRIX_MEANINGS[cell.functionKey][cell.position];
  const patternLabel = `${functionLabel} ${cell.position}`;
  const [isHelpActive, setIsHelpActive] = React.useState(false);

  return (
    <div
      className={cn(
        "group/matrix-cell relative z-0 min-h-[7rem] overflow-visible border border-transparent text-left transition hover:z-20 hover:border-sky-300/80 hover:brightness-[0.99]",
        isHelpActive && "z-40",
        TEAM_MATRIX_HEAT_CLASSES[cell.heat],
      )}
      aria-label={`${cell.count} people in ${patternLabel}`}
    >
      <div className="relative z-[1] flex min-h-[7rem] flex-col justify-between p-3">
        <div className="flex items-start justify-between gap-3">
          <span className="text-2xl font-semibold leading-none tracking-tight text-slate-950 tabular-nums md:text-[1.7rem]">
            {cell.count}
          </span>

          <div className="group/help relative">
            <button
              type="button"
              onPointerEnter={() => setIsHelpActive(true)}
              onPointerLeave={() => setIsHelpActive(false)}
              onFocus={() => setIsHelpActive(true)}
              onBlur={() => setIsHelpActive(false)}
              className="flex size-6 shrink-0 items-center justify-center rounded-full border border-sky-200/80 bg-white/80 text-sky-700 shadow-[0_1px_0_rgba(14,165,233,0.08)] transition hover:bg-sky-50 hover:text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              aria-label="What this position means"
            >
              <CircleHelp className="size-3.5" />
            </button>

            <div className="pointer-events-none absolute right-0 top-8 z-50 w-[min(20rem,calc(100vw-3rem))] translate-y-1 rounded-lg border border-slate-200 bg-white p-3 text-left opacity-0 shadow-[0_18px_40px_-26px_rgba(15,23,42,0.32)] transition duration-150 group-hover/help:translate-y-0 group-hover/help:opacity-100 group-focus-within/help:translate-y-0 group-focus-within/help:opacity-100">
              <p className="text-sm font-medium text-slate-950">
                {functionLabel} {cell.position}
                {position ? ` · ${position.title}` : null}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-600">{meaning}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex min-h-7 items-end gap-1.5 overflow-hidden">
          {visibleMembers.length > 0 ? (
            visibleMembers.map((member) => (
              <TeamMatrixAvatar key={member.name} member={member} />
            ))
          ) : overflowCount === 0 ? (
            <span className="text-xs text-slate-500">No members</span>
          ) : null}

          {overflowCount > 0 ? (
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-950 text-[10px] font-semibold text-white ring-1 ring-white">
              +{overflowCount}
            </span>
          ) : null}
        </div>
      </div>

      {members.length > 0 ? (
        <div
          className={cn(
            "pointer-events-none absolute left-3 right-3 bottom-3 z-20 translate-y-1 rounded-md border border-slate-200 bg-white p-2 opacity-0 shadow-[0_14px_30px_-22px_rgba(15,23,42,0.28)] transition duration-150",
            !isHelpActive
              && "group-hover/matrix-cell:translate-y-0 group-hover/matrix-cell:opacity-100",
          )}
        >
          <div className="space-y-1">
            {members.slice(0, 3).map((member) => (
              <div key={`${cell.position}-${cell.functionKey}-${member.name}`} className="flex min-w-0 items-center gap-2">
                <TeamMatrixAvatar member={member} size="md" />
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-slate-950">{member.name}</p>
                  <p className="truncate text-[11px] text-slate-500">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TeamMatrixAvatar({
  member,
  size = "sm",
}: {
  member: PreviewPerson;
  size?: "sm" | "md";
}) {
  return (
    <span
      className={cn(
        "relative shrink-0 overflow-hidden rounded-full border border-white bg-slate-100 shadow-sm",
        size === "md" ? "size-8" : "size-6",
      )}
      aria-label={member.name}
    >
      <Image
        src={member.avatarSrc}
        alt={member.name}
        fill
        sizes={size === "md" ? "32px" : "24px"}
        className="object-cover"
      />
    </span>
  );
}

function buildTeamMatrixMembersByCell(members: PreviewPerson[]) {
  const membersByCell = TEAM_MATRIX_POSITIONS.map(() =>
    TEAM_MATRIX_FUNCTIONS.map(() => [] as PreviewPerson[]),
  );

  for (const member of members) {
    member.profileAxes.slice(0, 4).forEach((axis, index) => {
      const functionKey = normalizedTeamMatrixFunctionKey(axis);

      if (!functionKey) {
        return;
      }

      const functionPosition = TEAM_MATRIX_FUNCTION_INDEX.get(functionKey);

      if (functionPosition === undefined) {
        return;
      }

      membersByCell[index]?.[functionPosition]?.push(member);
    });
  }

  return membersByCell;
}

function buildTeamMatrixCellsByKey(members: PreviewPerson[]) {
  const matrix = new Map<string, TeamMatrixCellDiagnosis>();
  const membersByCell = buildTeamMatrixMembersByCell(members);
  const expected = members.length / 4;

  TEAM_MATRIX_POSITIONS.forEach(({ position }, positionIndex) => {
    TEAM_MATRIX_FUNCTIONS.forEach(({ key }, functionPosition) => {
      const count = membersByCell[positionIndex]?.[functionPosition]?.length ?? 0;

      matrix.set(`${position}:${key}`, {
        position,
        functionKey: key,
        count,
        heat: teamMatrixHeatIndex(count, expected),
      });
    });
  });

  return matrix;
}

function normalizedTeamMatrixFunctionKey(value: string): MatrixFunctionKey | null {
  const normalized = value.trim().replace(/\s+/g, "");

  if (normalized === "D" || normalized === "Д") {
    return "D";
  }

  if (normalized === "R" || normalized === "М") {
    return "R";
  }

  if (normalized === "C" || normalized === "К") {
    return "C";
  }

  if (normalized.toLowerCase() === "ca" || normalized === "Р") {
    return "Ca";
  }

  return null;
}

function teamMatrixHeatIndex(count: number, expected: number) {
  if (count === 0 || expected <= 0) {
    return 0;
  }

  const ratio = count / expected;

  if (ratio <= 1) {
    return 1;
  }

  if (ratio <= 1.15) {
    return 2;
  }

  if (ratio <= 1.35) {
    return 3;
  }

  if (ratio <= 1.6) {
    return 4;
  }

  if (ratio <= 1.9) {
    return 5;
  }

  return 6;
}

export function FundamentalsPreview({
  variant,
}: {
  variant: "compact" | "expanded";
}) {
  const compact = variant === "compact";
  const items = React.useMemo(
    (): InsightSurfaceItemConfig[] => {
      const sectionById = new Map(
        sixPillarsFundamentalsPreview.sections.map((section) => [section.id, section]),
      );

      const weakestAreaSection = sectionById.get("weakest-area");
      const strongestAreaSection = sectionById.get("strongest-area");

      const baseItems: InsightSurfaceItemConfig[] = [
        {
          id: "weakest-area",
          title: "The Weakest Areas",
          preview:
            weakestAreaSection?.preview
            ?? "Accountability is the softest category: peer challenge is present, but not yet consistent.",
          icon: "shield",
          tone: "warning",
          defaultOpen: true,
          content: (
            <FundamentalsAreaContent
              area={sixPillarsFundamentalsPreview.weakestArea}
              statementTitle="Bottom Statements"
              recommendationsTitle="Recommendations"
            />
          ),
        },
        {
          id: "strongest-area",
          title: "The Strongest Areas",
          preview:
            strongestAreaSection?.preview
            ?? "Commitment is the current anchor: the team knows what matters and sticks to decisions.",
          icon: "sparkles",
          tone: "positive",
          content: (
            <FundamentalsAreaContent
              area={sixPillarsFundamentalsPreview.strongestArea}
              statementTitle="Top Statements"
              recommendationsTitle="Recommendations"
            />
          ),
        },
      ];

      const remainingSections = sixPillarsFundamentalsPreview.sections.filter(
        (section) =>
          section.id !== "weakest-area"
          && section.id !== "strongest-area"
          && section.id !== "key-differences"
          && section.id !== "statement-averages",
      );

      return [
        ...baseItems,
        ...remainingSections.map((section) => {
          const icon: InsightIconKey =
            section.id === "recommendations-by-category"
              ? "target"
              : section.id === "statement-averages"
                ? "chart"
                : "layers";

          return {
            id: section.id,
            title: section.title,
            preview: section.preview,
            icon,
            tone: "info" as const,
            content: <AppSectionBody section={section} />,
          };
        }),
      ];
    },
    [],
  );

  return (
    <div
      className={cn(
        "space-y-8 px-4 py-4 sm:px-5",
        compact ? "sm:py-5" : "sm:px-6 sm:py-6",
      )}
    >
      <FundamentalsStage />

      <AppInsightSurface items={items} />
    </div>
  );
}

export function PulsePreview({
  variant,
}: {
  variant: "compact" | "expanded";
}) {
  const compact = variant === "compact";
  const [activeTab, setActiveTab] = React.useState<"ok" | "bad">("ok");
  const activeItems = sixPillarsPulsePreview.feedback[activeTab];

  return (
    <div
      className={cn(
        "space-y-8 px-4 py-4 sm:px-5",
        compact ? "sm:py-5" : "sm:px-6 sm:py-6",
      )}
    >
      <PulseStage />

      <div className="space-y-4">
        <div>
          <div className="border-border/70 bg-muted/60 grid h-auto w-full grid-cols-2 rounded-xl border p-1 sm:max-w-md">
            <button
              type="button"
              onClick={() => setActiveTab("ok")}
              className={cn(
                "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium sm:px-4",
                activeTab === "ok"
                  ? "bg-white text-slate-950 shadow-[0_10px_20px_-18px_rgba(15,23,42,0.2)]"
                  : "text-slate-600 hover:text-slate-950",
              )}
            >
              <span className="truncate">OK Details</span>
              <MiniCount>{sixPillarsPulsePreview.feedbackCounts.ok}</MiniCount>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("bad")}
              className={cn(
                "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium sm:px-4",
                activeTab === "bad"
                  ? "bg-white text-slate-950 shadow-[0_10px_20px_-18px_rgba(15,23,42,0.2)]"
                  : "text-slate-600 hover:text-slate-950",
              )}
            >
              <span className="truncate">Not OK Details</span>
              <MiniCount>{sixPillarsPulsePreview.feedbackCounts.bad}</MiniCount>
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg border border-border">
          <div className="overflow-auto">
            <table className="w-full min-w-[30rem] table-fixed text-left text-sm">
              <colgroup>
                <col className="w-14 sm:w-20" />
                <col />
                <col className="w-28 sm:w-36" />
              </colgroup>
              <thead>
                <tr className="border-b border-border">
                  <th className="h-10 px-4 font-medium text-muted-foreground">#</th>
                  <th className="h-10 px-4 font-medium text-muted-foreground">
                    {activeTab === "ok" ? "OK Details" : "Not OK Details"}
                  </th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground">
                    Cycle
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeItems.map((item) => (
                  <tr
                    key={`${activeTab}-${item.id}-${item.when}`}
                    className="border-t border-border first:border-t-0"
                  >
                    <td className="px-4 py-3.5 align-top font-medium tabular-nums">{item.id}</td>
                    <td className="px-4 py-3.5 break-words whitespace-normal">{item.text}</td>
                    <td className="px-4 py-3.5 pl-6 text-right align-top tabular-nums">
                      {item.when}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CandidatesPreview({
  variant,
}: {
  variant: "compact" | "expanded";
}) {
  const compact = variant === "compact";
  const items = React.useMemo(
    (): InsightSurfaceItemConfig[] =>
      sixPillarsCandidatesPreview.sections.map((section, index) => ({
        id: section.id,
        title: section.title,
        preview: section.preview,
        icon:
          section.id === "summary"
            ? "compass"
            : section.id === "current-team-vs-candidate"
              ? "users"
              : section.id === "will-this-work-out"
                ? "target"
                : section.id === "role-and-profile-fit"
                  ? "chart"
                  : section.id === "team-impact"
                    ? "layers"
                    : section.id === "onboarding-plan"
                      ? "target"
                      : section.id === "hire-recommendations" ||
                          section.id === "bottom-line"
                        ? "sparkles"
                        : "compass",
        tone:
          section.id === "summary"
            ? "neutral"
            : section.id === "role-and-profile-fit" ||
                section.id === "hire-recommendations" ||
                section.id === "bottom-line"
              ? "positive"
              : "info",
        defaultOpen: index === 0,
        content: <AppSectionBody section={section} />,
      })),
    [],
  );

  return (
    <div
      className={cn(
        "px-4 py-4 sm:px-5",
        compact ? "sm:py-5" : "sm:px-6 sm:py-6",
      )}
    >
      <AppInsightSurface items={items} />
    </div>
  );
}

function AppInsightSurface({
  items,
}: {
  items: InsightSurfaceItemConfig[];
}) {
  const [openItems, setOpenItems] = React.useState<string[]>(() =>
    items.filter((item) => item.defaultOpen).map((item) => item.id),
  );

  React.useEffect(() => {
    setOpenItems((current) => {
      const available = new Set(items.map((item) => item.id));
      const next = current.filter((itemId) => available.has(itemId));

      for (const item of items) {
        if (item.defaultOpen && !next.includes(item.id)) {
          next.push(item.id);
        }
      }

      return next;
    });
  }, [items]);

  return (
    <AccordionPrimitive.Root type="multiple" value={openItems} onValueChange={setOpenItems} className="w-full">
      {items.map((item, index) => {
        const tone = toneStyles[item.tone ?? "neutral"];
        const Icon = insightIconMap[item.icon];

        return (
          <AccordionPrimitive.Item
            key={item.id}
            value={item.id}
            className="border-b border-slate-200/80 last:border-b-0"
          >
            <AccordionPrimitive.Header>
              <AccordionPrimitive.Trigger
                className={cn(
                  "group/insight flex w-full items-start justify-between gap-3 text-left hover:no-underline sm:gap-4",
                  index === 0 ? "pb-4 pt-0 sm:pb-5 sm:pt-0" : "py-4 sm:py-5",
                )}
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div
                    className={cn(
                      "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border bg-white sm:size-8",
                      tone.iconWrapperClassName,
                    )}
                  >
                    <Icon className={cn("size-3.5", tone.iconClassName)} />
                  </div>

                  <div className="min-w-0 space-y-1">
                    <h3 className="text-lg font-semibold tracking-tight text-slate-950 sm:text-xl">
                      {item.title}
                    </h3>
                    <p className="line-clamp-2 text-sm leading-6 text-slate-600 group-data-[state=open]/insight:hidden sm:line-clamp-1">
                      {item.preview}
                    </p>
                  </div>
                </div>

                <AccordionChevronCircle groupClassName="group-data-[state=open]/insight:text-foreground/82 group-data-[state=open]/insight:border-border/80">
                  <ChevronDown className="size-4 transition-transform duration-200 group-data-[state=open]/insight:rotate-180" />
                </AccordionChevronCircle>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className="pb-6 pl-0 sm:pb-8 sm:pl-11">{item.content}</div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        );
      })}
    </AccordionPrimitive.Root>
  );
}

function AppSectionBody({ section }: { section: PreviewSection }) {
  if (section.groups?.length) {
    return <PreviewSectionGroups groups={section.groups} />;
  }

  return (
    <div className="border-l border-slate-200/90 pl-4 md:pl-5">
      <SectionContentRows sectionId={section.id} paragraphs={section.paragraphs} lists={section.lists} />
    </div>
  );
}

function PreviewSectionGroups({
  groups,
}: {
  groups: NonNullable<PreviewSection["groups"]>;
}) {
  const [openItems, setOpenItems] = React.useState<string[]>(["group-0"]);

  return (
    <AccordionPrimitive.Root
      type="multiple"
      value={openItems}
      onValueChange={setOpenItems}
      className="w-full"
    >
      {groups.map((group, index) => (
        <FundamentalsNestedItem
          key={`${group.title}-${index}`}
          value={`group-${index}`}
          index={index}
          title={group.title}
          badge={group.badge}
          tone={group.tone ?? "neutral"}
        >
          <div className="border-l border-slate-200/90 pl-4 md:pl-5">
            <SectionContentRows
              sectionId={group.title}
              paragraphs={group.paragraphs}
              lists={group.lists}
            />
          </div>
        </FundamentalsNestedItem>
      ))}
    </AccordionPrimitive.Root>
  );
}

function SectionContentRows({
  sectionId,
  paragraphs,
  lists,
}: {
  sectionId: string;
  paragraphs?: string[];
  lists?: PreviewSection["lists"];
}) {
  return (
    <div className="space-y-4">
      {paragraphs?.length ? (
        <div className="max-w-[86ch] space-y-3">
          {paragraphs.map((paragraph, index) => (
            <MentionText
              key={`${sectionId}-paragraph-${index}`}
              text={paragraph}
              className="text-sm leading-[1.6] whitespace-pre-wrap text-slate-700"
            />
          ))}
        </div>
      ) : null}

      {lists?.map((list) => (
        <div
          key={`${sectionId}-${list.label ?? "list"}`}
          className="divide-y divide-slate-200/80"
        >
          {list.style === "actionable" ? (
            list.label ? (
              <InsightDetailLine label={list.label}>
                <ActionableInsightList
                  sectionId={sectionId}
                  items={list.items}
                />
              </InsightDetailLine>
            ) : (
              <ActionableInsightList
                sectionId={sectionId}
                items={list.items}
              />
            )
          ) : (
            <InsightDetailLine label={list.label ?? "Details"}>
              <ul className="space-y-3">
                {list.items.map((item, index) => {
                  const content =
                    typeof item === "string" ? { text: item, meta: undefined } : item;

                  return (
                    <li
                      key={`${sectionId}-${list.label ?? "list"}-${index}`}
                      className="flex items-start gap-3"
                    >
                      <span className="mt-3 size-1.5 shrink-0 rounded-full bg-slate-300" />
                      <div className="min-w-0 max-w-[86ch] flex-1">
                        <MentionText
                          text={content.text}
                          className="text-sm leading-[1.6] whitespace-pre-wrap text-slate-700"
                        />
                      </div>
                      {content.meta ? (
                        <MiniOutlineBadge className="shrink-0 border-slate-200 bg-white text-slate-700">
                          {content.meta}
                        </MiniOutlineBadge>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </InsightDetailLine>
          )}
        </div>
      ))}
    </div>
  );
}

function ActionableInsightList({
  sectionId,
  items,
}: {
  sectionId: string;
  items: PreviewListItem[];
}) {
  return (
    <ul className="space-y-2">
      {items.map((item, index) => {
        const content =
          typeof item === "string" ? { text: item, meta: undefined } : item;

        return (
          <li
            key={`${sectionId}-actionable-${index}`}
            className="grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-2.5"
          >
            <span
              className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[0.4rem] border border-slate-200/90 bg-slate-50 text-slate-500"
              aria-hidden
            >
              <ListTodo className="size-3" />
            </span>

            <div className="min-w-0 max-w-[86ch]">
              <MentionText
                text={content.text}
                className="text-sm leading-6 whitespace-pre-wrap text-slate-700"
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function FundamentalsAreaContent({
  area,
  statementTitle,
  recommendationsTitle,
}: {
  area: {
    category: string;
    average: number;
    interpretation: string;
    statements: readonly {
      text: string;
      meta: string;
    }[];
    recommendations: {
      quickWins: readonly string[];
      longTerm: readonly string[];
    };
  };
  statementTitle: string;
  recommendationsTitle: string;
}) {
  const [openItems, setOpenItems] = React.useState<string[]>([
    `${area.category}-overview`,
  ]);

  return (
    <AccordionPrimitive.Root
      type="multiple"
      value={openItems}
      onValueChange={setOpenItems}
      className="w-full"
    >
      <FundamentalsNestedItem
        value={`${area.category}-overview`}
        index={0}
        title={area.category}
        badge={`${area.average.toFixed(1)}/5`}
        tone={fundamentalsAverageTone(area.average)}
      >
        <div className="border-l border-slate-200/90 pl-4 md:pl-5">
          <MentionText
            text={area.interpretation}
            className="max-w-[86ch] text-sm leading-[1.6] whitespace-pre-wrap text-slate-700"
          />
        </div>
      </FundamentalsNestedItem>

      <FundamentalsNestedItem
        value={`${area.category}-statements`}
        index={1}
        title={statementTitle}
      >
        <div className="border-l border-slate-200/90 pl-4 md:pl-5">
          <div className="divide-y divide-slate-200/80">
            {area.statements.map((statement, index) => (
              <div key={`${area.category}-statement-${index}`} className="py-3">
                <MentionText
                  text={statement.text}
                  className="max-w-[86ch] text-[15px] leading-[1.55] whitespace-pre-wrap text-slate-700"
                />
                <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[12px] leading-5 text-slate-500">
                  <span>Average score:</span>
                  <span className="font-medium tabular-nums text-slate-700">
                    {statement.meta}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </FundamentalsNestedItem>

      <FundamentalsNestedItem
        value={`${area.category}-recommendations`}
        index={2}
        title={recommendationsTitle}
      >
        <div className="border-l border-slate-200/90 pl-4 md:pl-5">
          <div className="divide-y divide-slate-200/80">
            <InsightDetailLine label="Quick Wins">
              <ActionableInsightList
                sectionId={`${area.category}-quick-wins`}
                items={area.recommendations.quickWins.map((text) => ({ text }))}
              />
            </InsightDetailLine>
            <InsightDetailLine label="Long Term">
              <ActionableInsightList
                sectionId={`${area.category}-long-term`}
                items={area.recommendations.longTerm.map((text) => ({ text }))}
              />
            </InsightDetailLine>
          </div>
        </div>
      </FundamentalsNestedItem>
    </AccordionPrimitive.Root>
  );
}

function FundamentalsNestedItem({
  value,
  index,
  title,
  badge,
  tone = "neutral",
  children,
}: React.PropsWithChildren<{
  value: string;
  index: number;
  title: string;
  badge?: string;
  tone?: InsightTone;
}>) {
  return (
    <AccordionPrimitive.Item
      value={value}
      className="border-t border-slate-200/80 first:border-t-0"
    >
      <AccordionPrimitive.Header>
        <AccordionPrimitive.Trigger className="group/fundamentals flex w-full items-start gap-3 py-4 text-left hover:no-underline sm:gap-4 sm:py-5">
          <div className="grid min-w-0 flex-1 grid-cols-[2.25rem_minmax(0,1fr)] items-start gap-3 sm:grid-cols-[2.5rem_minmax(0,1fr)]">
            <OrdinalPill index={index} />

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-[0.98rem] leading-6 font-semibold tracking-tight text-slate-950 sm:text-base">
                  {title}
                </h4>
                {badge ? (
                  <AreaScoreBadge tone={tone}>
                    {badge}
                  </AreaScoreBadge>
                ) : null}
              </div>
            </div>
          </div>

          <ChevronDown className="text-muted-foreground pointer-events-none mt-0.5 size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/fundamentals:rotate-180" />
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>

      <AccordionPrimitive.Content className="overflow-hidden pb-4 pl-[calc(2.25rem+0.75rem)] sm:pb-5 sm:pl-[calc(2.5rem+0.75rem)] data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        {children}
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

export function SignalsTrigger({
  signals,
  evidence,
}: {
  signals: PreviewSignalGroups;
  evidence?: string | null;
}) {
  const count = getPreviewEvidenceCount(signals);

  if (count === 0 && !evidence) {
    return null;
  }

  return (
    <FloatingCardTrigger
      label={<EvidencePill count={count} />}
      cardClassName="w-[min(22rem,calc(100vw-2rem))]"
      card={<EvidenceCard signals={signals} evidence={evidence} />}
    />
  );
}

function EvidenceCard({
  signals,
  evidence,
}: {
  signals: PreviewSignalGroups;
  evidence?: string | null;
}) {
  const sections = [
    {
      label: "Profiles",
      items: signals.profiles,
    },
    {
      label: "Fundamentals",
      items: signals.fundamentals,
    },
    {
      label: "Pulse",
      items: signals.pulse,
    },
  ];

  return (
    <div className="overflow-hidden rounded-[1.4rem] border border-border/70 bg-background shadow-[0_26px_64px_-34px_rgba(15,23,42,0.34)]">
      <div className="divide-y divide-border/60 px-5 py-3">
        {evidence ? (
          <div className="py-4 first:pt-1 last:pb-1">
            <MentionText
              text={evidence}
              className="text-[0.9rem] leading-[1.65rem] text-foreground/76"
            />
          </div>
        ) : null}

        {sections.map((section) => (
          <div key={section.label} className="py-4 first:pt-1 last:pb-1">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-foreground/72">
                {section.label}
              </p>
              <MiniCount>
                {section.items.length}
              </MiniCount>
            </div>

            {section.items.length === 0 ? (
              <p className="text-[0.9rem] leading-[1.65rem] text-foreground/52">
                No supporting signals yet.
              </p>
            ) : (
              <div className="space-y-3">
                {section.items.map((signal, index) => (
                  <MentionText
                    key={`${section.label}-${index}`}
                    text={signal}
                    className="text-[0.9rem] leading-[1.65rem] text-foreground/76"
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MentionText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const names = [...sixPillarsPreviewPeople]
    .map((person) => person.name)
    .sort((left, right) => right.length - left.length);
  const matcher = names.length
    ? new RegExp(`(${names.map(escapeRegex).join("|")})`, "g")
    : null;
  const parts: Array<string | { name: string }> = [];

  if (!matcher) {
    parts.push(text);
  } else {
    let cursor = 0;

    for (const match of text.matchAll(matcher)) {
      const matchText = match[0];
      const index = match.index ?? 0;

      if (cursor < index) {
        parts.push(text.slice(cursor, index));
      }

      parts.push({ name: matchText });
      cursor = index + matchText.length;
    }

    if (cursor < text.length) {
      parts.push(text.slice(cursor));
    }

    if (!parts.length) {
      parts.push(text);
    }
  }

  return (
    <div className={className}>
      {parts.map((part, index) =>
        typeof part === "string" ? (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        ) : (
          <PersonMention key={`${part.name}-${index}`} name={part.name} />
        ),
      )}
    </div>
  );
}

export function OwnerMention({ name }: { name: string }) {
  const person = getSixPillarsPreviewPerson(name);

  if (!person) {
    return <span className="font-medium text-foreground">{name}</span>;
  }

  return <PersonMention name={name} emphasis="highlighted" />;
}

function PersonMention({
  name,
  emphasis = "default",
}: {
  name: string;
  emphasis?: "default" | "highlighted";
}) {
  const person = getSixPillarsPreviewPerson(name);

  if (!person) {
    return <span>{name}</span>;
  }

  return (
    <FloatingCardTrigger
      label={
        <span className="inline-flex cursor-pointer items-baseline gap-[0.28em] font-medium text-foreground transition-colors duration-150 hover:text-foreground/72">
          <span
            className={cn(
              "relative size-[1.2rem] shrink-0 self-center overflow-hidden rounded-full",
              avatarToneClasses[person.avatarTone],
              emphasis === "highlighted"
                ? "animate-[roadmap-attention-pulse_2.2s_ease-in-out_infinite]"
                : "",
            )}
          >
            <Image src={person.avatarSrc} alt="" fill sizes="20px" className="object-cover" />
          </span>
          <span>{name}</span>
        </span>
      }
      cardClassName="w-[min(22rem,calc(100vw-2rem))]"
      card={<PersonCard person={person} />}
    />
  );
}

function PersonCard({ person }: { person: PreviewPerson }) {
  if (person.type === "candidate") {
    return (
      <div className="overflow-hidden rounded-[1.4rem] border border-border/70 bg-background shadow-[0_26px_64px_-34px_rgba(15,23,42,0.34)]">
        <div className="border-b border-border/60 bg-slate-50/90 px-4 py-4">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "relative size-11 shrink-0 overflow-hidden rounded-full border border-white/70 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.45)]",
                avatarToneClasses[person.avatarTone],
              )}
            >
              <Image
                src={person.avatarSrc}
                alt={person.name}
                fill
                sizes="44px"
                className="object-cover"
              />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-[1.02rem] font-semibold tracking-[-0.03em] text-foreground">
                {person.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{person.role}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border/60 px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <MiniBadge>{person.candidateStage ?? "Candidate"}</MiniBadge>
            <p className="text-[0.73rem] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              {person.profileAxes.join(" - ")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const fundamentalsScore = getRatioPercent(
    person.fundamentalsCompleted,
    person.fundamentalsTotal,
  );
  const checkinsScore = getRatioPercent(
    person.checkinsCompleted,
    person.checkinsTotal,
  );

  return (
    <div className="overflow-hidden rounded-[1.4rem] border border-border/70 bg-background shadow-[0_26px_64px_-34px_rgba(15,23,42,0.34)]">
      <div className="border-b border-border/60 bg-slate-50/90 px-4 py-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "relative size-11 shrink-0 overflow-hidden rounded-full border border-white/70 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.45)]",
              avatarToneClasses[person.avatarTone],
            )}
          >
            <Image
              src={person.avatarSrc}
              alt={person.name}
              fill
              sizes="44px"
              className="object-cover"
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[1.02rem] font-semibold tracking-[-0.03em] text-foreground">
              {person.name}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>{person.role}</span>
              {person.type === "lead" ? <MiniBadge>Lead</MiniBadge> : null}
              {person.isCoLead ? <MiniBadge>Co-lead</MiniBadge> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/60 px-4 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-[1.02rem] font-semibold tracking-[-0.03em] text-foreground">
            {person.profileTitle}
          </p>
          <p className="text-[0.73rem] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {person.profileAxes.join(" - ")}
          </p>
        </div>
      </div>

      <div className="border-t border-border/60 px-4 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <MetricBlock
            label="Fundamentals"
            value={`${fundamentalsScore}% (${person.fundamentalsCompleted}/${person.fundamentalsTotal})`}
          />
          <MetricBlock
            label="Checkins"
            value={`${checkinsScore}% (${person.checkinsCompleted}/${person.checkinsTotal})`}
          />
        </div>
      </div>
    </div>
  );
}

function FloatingCardTrigger({
  label,
  card,
  cardClassName,
}: {
  label: React.ReactNode;
  card: React.ReactNode;
  cardClassName?: string;
}) {
  const boundary = React.useContext(FloatingBoundaryContext);
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [interactionMode, setInteractionMode] = React.useState<
    "mouse" | "touch" | "keyboard"
  >("mouse");
  const closeTimerRef = React.useRef<number | null>(null);
  const interactionModeRef = React.useRef<"mouse" | "touch" | "keyboard">(
    "mouse",
  );

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openCard = React.useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, 120);
  }, [clearCloseTimer]);

  React.useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const trigger = triggerRef.current;
      const panel = document.querySelector("[data-six-pillars-floating-card='true']");

      if (
        trigger &&
        (trigger === event.target || trigger.contains(event.target as Node))
      ) {
        return;
      }

      if (panel && panel.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        onPointerDown={(event) => {
          event.stopPropagation();
          const nextMode = event.pointerType === "mouse" ? "mouse" : "touch";
          interactionModeRef.current = nextMode;
          setInteractionMode(nextMode);
        }}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") {
            interactionModeRef.current = "mouse";
            setInteractionMode("mouse");
            openCard();
          }
        }}
        onPointerLeave={() => {
          if (interactionModeRef.current === "mouse") {
            scheduleClose();
          }
        }}
        onFocus={(event) => {
          event.stopPropagation();
          if (interactionModeRef.current !== "touch") {
            interactionModeRef.current = "keyboard";
            setInteractionMode("keyboard");
            openCard();
          }
        }}
        onBlur={(event) => {
          if (
            interactionModeRef.current !== "touch" &&
            !event.currentTarget.contains(event.relatedTarget)
          ) {
            scheduleClose();
          }
        }}
        className="inline-flex rounded-[0.4rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {label}
      </span>

      {mounted && open && boundary?.boundaryRef.current
        ? createPortal(
            <FloatingCardPortal
              anchorRef={triggerRef}
              boundaryRef={boundary.boundaryRef}
              className={cardClassName}
              showBackdrop={interactionMode === "touch"}
              onRequestClose={() => {
                clearCloseTimer();
                setOpen(false);
              }}
              onPointerEnter={openCard}
              onPointerLeave={scheduleClose}
            >
              {card}
            </FloatingCardPortal>,
            boundary.boundaryRef.current,
          )
        : null}
    </>
  );
}

function FloatingCardPortal({
  anchorRef,
  boundaryRef,
  className,
  showBackdrop,
  onRequestClose,
  onPointerEnter,
  onPointerLeave,
  children,
}: React.PropsWithChildren<{
  anchorRef: React.RefObject<HTMLElement | null>;
  boundaryRef: React.RefObject<HTMLDivElement | null>;
  className?: string;
  showBackdrop?: boolean;
  onRequestClose: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}>) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({
    left: -9999,
    top: -9999,
    maxHeight: 9999,
  });

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const boundary = boundaryRef.current;
      const panel = panelRef.current;

      if (!anchor || !boundary || !panel) {
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const gutter = 12;
      const gap = 10;
      const availableBelow =
        boundaryRect.bottom - anchorRect.bottom - gutter - gap;
      const availableAbove =
        anchorRect.top - boundaryRect.top - gutter - gap;
      const fitsBelow = panelRect.height <= availableBelow;
      const fitsAbove = panelRect.height <= availableAbove;

      let left = anchorRect.left - boundaryRect.left;
      const maxLeft = Math.max(
        gutter,
        boundaryRect.width - panelRect.width - gutter,
      );

      if (left < gutter) {
        left = gutter;
      } else if (left > maxLeft) {
        left = maxLeft;
      }

      const placeBelow =
        fitsBelow || (!fitsAbove && availableBelow >= availableAbove);
      const availableHeight = Math.max(
        160,
        Math.floor(placeBelow ? availableBelow : availableAbove),
      );
      const renderedHeight = Math.min(panelRect.height, availableHeight);

      let top = placeBelow
        ? anchorRect.bottom - boundaryRect.top + gap
        : anchorRect.top - boundaryRect.top - renderedHeight - gap;

      const maxTop = boundaryRect.height - renderedHeight - gutter;

      if (top > maxTop) {
        top = maxTop;
      }

      if (top < gutter) {
        top = gutter;
      }

      setPosition({
        left,
        top,
        maxHeight: availableHeight,
      });
    };

    let animationFrame: number | null = null;
    const schedulePositionUpdate = () => {
      if (animationFrame !== null) {
        return;
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        updatePosition();
      });
    };

    updatePosition();
    window.addEventListener("resize", schedulePositionUpdate);
    window.addEventListener("scroll", schedulePositionUpdate, true);
    const resizeObserver = new ResizeObserver(schedulePositionUpdate);

    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
    }
    if (boundaryRef.current) {
      resizeObserver.observe(boundaryRef.current);
    }

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener("resize", schedulePositionUpdate);
      window.removeEventListener("scroll", schedulePositionUpdate, true);
      resizeObserver.disconnect();
    };
  }, [anchorRef, boundaryRef]);

  return (
    <>
      {showBackdrop ? (
        <button
          type="button"
          aria-label="Close details"
          className="fixed inset-0 z-[29] bg-transparent"
          onPointerDown={(event) => {
            event.preventDefault();
            onRequestClose();
          }}
        />
      ) : null}

      <div
        ref={panelRef}
        data-six-pillars-floating-card="true"
        className={cn(
          "absolute z-[30] cursor-default overflow-y-auto overscroll-contain",
          className,
        )}
        style={{
          left: `${position.left}px`,
          top: `${position.top}px`,
          maxHeight: `${position.maxHeight}px`,
        }}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        {children}
      </div>
    </>
  );
}
